import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment-timezone';
import { FC, useCallback, useMemo, useState } from 'react';
import { AccountService } from '../../services/account/account-service';
import {
  AppointmentStatus,
  Contact,
  ContactApi,
  ContactLocationInfo,
  PatientInfo,
  PatientPreferencesResponse
} from '../../services/api-client/contact-api';
import AppointmentService from '../../services/appointment/appointment-service';
import { AppointmentSlotItem, PatientAppointment } from '../../services/appointment/types';
import { ILocation } from '../../services/location-service/location-service';
import PaymentService from '../../services/payment-service/payment-service';
import { CareCreditPatientPrescreenInfo } from '../../types/payment/care-credit';
import { PatientProfileContext } from './context';
import { useFeatureFlags } from '../feature-flags/use-feature-flags';

const getAppointment = (visits: PatientAppointment[]) => {
  const visit = visits
    .filter((visit) => visit.status !== AppointmentStatus.Cancelled)
    .sort((a, b) => moment(b.date).diff(moment(a.date)))[0];

  return visit;
};

interface PatientProfileProviderProps {
  children: React.ReactNode;
}

export const PatientProfileProvider: FC<PatientProfileProviderProps> = (props) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const [patientId, setPatientId] = useState<number>();
  const [locationId, setLocationId] = useState<number>(0);
  const [networkId, setNetworkId] = useState<number>(0);
  const [contactDetails, setContactDetails] = useState<Contact>(null!);
  const [contactLocation, setContactLocation] = useState<ILocation>();
  const { hasFeature } = useFeatureFlags();
  const [contactLocationInfo, setContactLocationInfo] = useState<ContactLocationInfo[]>([]);
  const [loggedInUserLocation, setLoggedInUserLocation] = useState<ILocation>();
  const [isLoadingAlerts, setIsLoadingAlerts] = useState<boolean>(false);
  const [patientInfo, setPatientInfo] = useState<PatientInfo>(null!);
  const [careCreditInfo, setCareCreditInfo] = useState<CareCreditPatientPrescreenInfo>(null!);
  const [patientPreferences, setPatientPreferences] = useState<PatientPreferencesResponse>(null!);
  const paymentApi = useMemo(() => new PaymentService(config, oidcService), [config, oidcService]);
  const appointmentApi = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  const loadSlotsNearPatientAppointment = async (locationId: number, date: string) => {
    try {
      const startDate = moment(date).hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss.SSS');
      const endDate = moment(date).hour(23).minute(59).second(0).format('YYYY-MM-DDTHH:mm:ss.SSS');

      const slots = await appointmentApi.getAppointmentSlots(locationId, startDate, endDate);
      return slots;
    } catch {
      // do nothing
    }

    return [] as AppointmentSlotItem[];
  };

  const isTemporaryUser = useMemo(() => {
    return (
      contactDetails?.foreignId?.startsWith('RwScheduler') ||
      contactDetails?.foreignId?.startsWith('Appt-') ||
      contactDetails?.foreignId?.startsWith('DEPRECATED-') ||
      contactDetails?.foreignId?.includes('-deleted-')
    );
  }, [contactDetails]);

  const contactLocationId = contactDetails?.location?.id;
  const hasForms = (locId: number) => hasFeature(locId, 'Forms2') || hasFeature(locId, 'Forms');
  const showFormsForPatient =
    contactLocationId &&
    hasForms(contactLocationId) &&
    locationId &&
    hasForms(locationId) &&
    !isTemporaryUser || false;

  const loadAlerts = async (patientId: number, locationId: number) => {
    setIsLoadingAlerts(true);
    try {
      const visits = await appointmentApi.getPatientAppointments(patientId, locationId);
      const appointmentToFindSlot = getAppointment(visits);

      const slots = appointmentToFindSlot
        ? await loadSlotsNearPatientAppointment(locationId, appointmentToFindSlot.date)
        : [];

      const appointmentInSlot = slots.find((slot) => slot.patientId === patientId);

      setPatientInfo((prev) => {
        if (prev.id !== patientId) return prev;

        const filteredAlerts =
          prev.patientAlerts?.filter((alert) => !alert.alert?.includes('broken appointments (past year)')) || [];

        const data = {
          ...prev,
          patientAlerts: [
            ...filteredAlerts,
            ...((appointmentInSlot?.noShowsInThePastYear || 0) > 0
              ? [
                  {
                    alert: `${appointmentInSlot!.noShowsInThePastYear} broken appointments (past year)`,
                    patientId: appointmentInSlot!.patientId
                  }
                ]
              : [])
          ]
        };

        return data;
      });
    } catch (error) {
      console.warn('Failed to load alerts: ', error);
    }
    setIsLoadingAlerts(false);
  };

  const loadPatientDataById = async (id: number) => {
    const claims = await oidcService.getClaims();
    const masterAccountId = claims.masterAccountId;
    const loggedInUserLocationId = claims.locationId;
    const contactApi = ContactApi.getInstance();
    const accountApi = AccountService.getInstance(config, oidcService);
    const [contactDetails, identicalContacts, patientInfo, patientPreferences, locations] = await Promise.all([
      contactApi.getContactDetailsById(id),
      contactApi.getIdenticalContacts(id),
      contactApi.getPatientInfoById(id),
      contactApi.getPatientPreferences(id),
      accountApi.getLocationsByMasterId(masterAccountId!)
    ]);
    const finalLocationId = locationId || contactDetails.location?.id || 0;

    try {
      const careCreditInfo = await paymentApi.getCareCreditPatientPrescreenInfo(finalLocationId, id);
      setCareCreditInfo(careCreditInfo);
    } catch (error) {
      console.warn('Failed to load CareCredit info: ', error);
    }

    // we are checking if we have access to the location we are trying to retrieve.
    // It possible we don't have access to the location that was passed in.
    const location = locations.filter((loc) => loc.id == finalLocationId).pop();
    const networkId = location?.idNetwork;
    const locationIds = new Set(locations.map((loc) => loc.id));
    const finalIdenticalContacts = identicalContacts.filter((ic) => locationIds.has(ic.locationId));

    let contactLocationInfo = [
      ...finalIdenticalContacts,
      {
        id: contactDetails.id,
        locationId: contactDetails.location!.id,
        locationName: contactDetails.location!.name,
        networkId: contactDetails.networkId
      }
    ].sort((a, b) => a.locationName!.localeCompare(b.locationName!));

    const dedupeMap = new Map();
    contactLocationInfo.forEach((item) => dedupeMap.set(item.locationId, item));
    contactLocationInfo = Array.from(dedupeMap.values());

    try {
      setPatientId(id);
      setContactDetails(contactDetails);
      setLoggedInUserLocation(locations.find((loc) => loc.id == loggedInUserLocationId));
      setContactLocation(location);
      setPatientInfo(patientInfo);
      setContactLocationInfo(contactLocationInfo);
      setPatientPreferences(patientPreferences);
      setLocationId(finalLocationId);
      setNetworkId(networkId || contactDetails.networkId);
    } catch (error: any) {
      console.log(error.message);
    }

    if (patientInfo) {
      loadAlerts(id, finalLocationId);
    }
  };

  const refreshPatientPreferences = useCallback(async () => {
    if (patientId) {
      const contactApi = ContactApi.getInstance();
      const patientPreferences = await contactApi.getPatientPreferences(patientId);
      setPatientPreferences(patientPreferences);
    }
  }, [patientId]);

  return (
    <PatientProfileContext.Provider
      value={{
        patientId,
        locationId,
        networkId,
        loadPatientDataById,
        loggedInUserLocation,
        contactDetails,
        contactLocation,
        contactLocationInfo,
        patientInfo,
        patientPreferences,
        refreshPatientPreferences,
        careCreditInfo,
        isLoadingAlerts,
        showFormsForPatient
      }}
    >
      {props.children}
    </PatientProfileContext.Provider>
  );
};
